import React from 'react';

// Shared presentational layout for the order / supplier-order detail pages.
// Both OrderDetail.tsx and SupplierOrderDetail.tsx import the common shell
// primitives here. Order-only sections may also use local layout primitives
// from this file when they need to stay visually aligned with the shared shell.

export const PAGE_SHELL_STYLE: React.CSSProperties = {
  background: 'var(--app-canvas)',
  borderRadius: 24,
  border: '1px solid var(--app-border)',
  padding: 20,
  overflowX: 'hidden',
};

export const OVERVIEW_HEADER_STYLE: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  gap: 16,
  padding: 18,
  marginBottom: 16,
  borderRadius: 18,
  background: 'var(--app-surface)',
  border: '1px solid var(--app-border)',
};

export const getOrderTypeHeaderStyle = (accentColor: string): React.CSSProperties => ({
  ...OVERVIEW_HEADER_STYLE,
  borderLeft: `5px solid ${accentColor}`,
  background: 'var(--app-surface)',
});

export const getOrderTypeBadgeStyle = (accentColor: string, accentBackground: string): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '7px 11px',
  borderRadius: 999,
  background: accentBackground,
  border: `1px solid ${accentColor}33`,
  color: accentColor,
  fontSize: 12,
  fontWeight: 800,
  letterSpacing: '0.08em',
  lineHeight: 1,
  textTransform: 'uppercase',
});

export const DETAIL_META_GRID_STYLE: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 10,
};

export const SECTION_CARD_STYLE: React.CSSProperties = {
  borderRadius: 20,
  border: '1px solid var(--app-border)',
  boxShadow: 'none',
  overflow: 'hidden',
};

export const SECTION_CARD_HEAD_STYLE: React.CSSProperties = {
  borderBottom: 'none',
  padding: '16px 18px 0',
};

export const SECTION_CARD_BODY_STYLE: React.CSSProperties = {
  padding: '14px 18px 18px',
};

export const INFO_GRID_STYLE: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  gap: 8,
};

export const ORDER_INFO_SUMMARY_GRID_STYLE: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 10,
  marginBottom: 16,
};

export const ORDER_INFO_SUMMARY_ITEM_STYLE: React.CSSProperties = {
  minWidth: 0,
  padding: '12px 14px',
  borderRadius: 8,
  border: '1px solid var(--app-border)',
  background: 'var(--app-surface)',
};

export const ORDER_INFO_ACCORDION_STYLE: React.CSSProperties = {
  borderRadius: 8,
  border: '1px solid var(--app-border)',
  background: 'var(--app-surface)',
  overflow: 'hidden',
};

export const DETAIL_ROW_GRID_STYLE: React.CSSProperties = {
  display: 'grid',
  gap: 0,
};

// Internal to renderInfoItem — callers compose via that helper, not by hand.
const INFO_ITEM_STYLE: React.CSSProperties = {
  minWidth: 0,
  padding: '10px 12px',
  borderRadius: 12,
  border: '1px solid var(--app-border)',
  background: 'var(--app-surface)',
};

const INFO_LABEL_STYLE: React.CSSProperties = {
  marginBottom: 5,
  color: 'var(--app-text-secondary)',
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1.35,
};

const INFO_VALUE_STYLE: React.CSSProperties = {
  minWidth: 0,
  color: 'var(--app-text)',
  fontSize: 14,
  fontWeight: 600,
  lineHeight: 1.35,
  wordBreak: 'break-word',
};

const DETAIL_ROW_STYLE: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 170px), 1fr))',
  alignItems: 'start',
  gap: 14,
  minWidth: 0,
  padding: '12px 0',
  borderBottom: '1px solid var(--app-border)',
};

export const STICKY_SIDEBAR_STYLE: React.CSSProperties = {
  position: 'sticky',
  // 64px frozen app header (Layout.tsx) + 24px gap, so the pinned sidebar
  // clears the sticky header instead of sliding under it.
  top: 88,
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
};

export const getStatusBadgeStyle = (status: string): React.CSSProperties => {
  // Status inks have one light/dark palette in App.css.
  const variants: Record<string, React.CSSProperties> = {
    NEW_INQUIRY: {
      background: 'rgba(100, 116, 139, 0.1)',
      border: '1px solid rgba(100, 116, 139, 0.18)',
      color: 'var(--status-ink-NEW_INQUIRY)',
    },
    QUOTE_SENT: {
      background: 'rgba(37, 99, 235, 0.1)',
      border: '1px solid rgba(37, 99, 235, 0.18)',
      color: 'var(--status-ink-QUOTE_SENT)',
    },
    CONFIRMED: {
      background: 'rgba(14, 116, 144, 0.1)',
      border: '1px solid rgba(14, 116, 144, 0.18)',
      color: 'var(--status-ink-CONFIRMED)',
    },
    IN_FULFILLMENT: {
      background: 'rgba(147, 51, 234, 0.1)',
      border: '1px solid rgba(147, 51, 234, 0.18)',
      color: 'var(--status-ink-IN_FULFILLMENT)',
    },
    READY_FOR_DISPATCH: {
      background: 'rgba(101, 163, 13, 0.1)',
      border: '1px solid rgba(101, 163, 13, 0.18)',
      color: 'var(--status-ink-READY_FOR_DISPATCH)',
    },
    IN_DELIVERY: {
      background: 'rgba(234, 88, 12, 0.1)',
      border: '1px solid rgba(234, 88, 12, 0.18)',
      color: 'var(--status-ink-IN_DELIVERY)',
    },
    PARTIALLY_DELIVERED: {
      background: 'rgba(217, 119, 6, 0.1)',
      border: '1px solid rgba(217, 119, 6, 0.18)',
      color: 'var(--status-ink-PARTIALLY_DELIVERED)',
    },
    ON_HOLD: {
      background: 'rgba(190, 24, 93, 0.1)',
      border: '1px solid rgba(190, 24, 93, 0.18)',
      color: 'var(--status-ink-ON_HOLD)',
    },
    DELIVERED: {
      background: 'rgba(5, 150, 105, 0.1)',
      border: '1px solid rgba(5, 150, 105, 0.18)',
      color: 'var(--status-ink-DELIVERED)',
    },
    // Closed by the invoice: ZK "Zamknięte fakturą", ZD "Zakończone" (WP3).
    INVOICED: {
      background: 'rgba(30, 58, 138, 0.1)',
      border: '1px solid rgba(30, 58, 138, 0.2)',
      color: 'var(--status-ink-INVOICED)',
    },
    // Supplier-order statuses (SUPPLIER_ORDER_STATUSES). DRAFT/CONFIRMED/CANCELLED
    // overlap with order statuses above; the rest are supplier-specific.
    DRAFT: {
      background: 'rgba(100, 116, 139, 0.1)',
      border: '1px solid rgba(100, 116, 139, 0.18)',
      color: 'var(--status-ink-DRAFT)',
    },
    SENT: {
      background: 'rgba(37, 99, 235, 0.1)',
      border: '1px solid rgba(37, 99, 235, 0.18)',
      color: 'var(--status-ink-SENT)',
    },
    IN_PRODUCTION: {
      background: 'rgba(217, 119, 6, 0.1)',
      border: '1px solid rgba(217, 119, 6, 0.18)',
      color: 'var(--status-ink-IN_PRODUCTION)',
    },
    READY: {
      background: 'rgba(101, 163, 13, 0.1)',
      border: '1px solid rgba(101, 163, 13, 0.18)',
      color: 'var(--status-ink-READY)',
    },
    SHIPPED: {
      background: 'rgba(234, 88, 12, 0.1)',
      border: '1px solid rgba(234, 88, 12, 0.18)',
      color: 'var(--status-ink-SHIPPED)',
    },
    PARTIALLY_RECEIVED: {
      background: 'rgba(217, 119, 6, 0.1)',
      border: '1px solid rgba(217, 119, 6, 0.18)',
      color: 'var(--status-ink-PARTIALLY_RECEIVED)',
    },
    RECEIVED: {
      background: 'rgba(5, 150, 105, 0.1)',
      border: '1px solid rgba(5, 150, 105, 0.18)',
      color: 'var(--status-ink-RECEIVED)',
    },
    CANCELLED: {
      background: 'rgba(220, 38, 38, 0.1)',
      border: '1px solid rgba(220, 38, 38, 0.18)',
      color: 'var(--status-ink-CANCELLED)',
    },
  };

  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    borderRadius: 999,
    fontWeight: 700,
    fontSize: 13,
    lineHeight: 1,
    ...variants[status],
  };
};

export const renderSectionTitle = (label: string) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
    <span
      style={{
        width: 8,
        height: 8,
        borderRadius: 999,
        background: '#2563eb',
        flex: '0 0 auto',
      }}
    />
    <span
      style={{
        fontSize: 17,
        fontWeight: 700,
        color: 'var(--app-text)',
      }}
    >
      {label}
    </span>
  </div>
);

export const renderInfoItem = (
  label: string,
  value: React.ReactNode,
  span: 1 | 2 = 1
) => (
  <div
    key={label}
    style={{
      ...INFO_ITEM_STYLE,
      gridColumn: span === 2 ? '1 / -1' : undefined,
    }}
  >
    <div style={INFO_LABEL_STYLE}>{label}</div>
    <div style={INFO_VALUE_STYLE}>{value}</div>
  </div>
);

export const renderSummaryItem = (label: string, value: React.ReactNode) => (
  <div key={label} style={ORDER_INFO_SUMMARY_ITEM_STYLE}>
    <div style={INFO_LABEL_STYLE}>{label}</div>
    <div style={INFO_VALUE_STYLE}>{value}</div>
  </div>
);

export const renderDetailRow = (label: string, value: React.ReactNode) => (
  <div key={label} style={DETAIL_ROW_STYLE}>
    <div style={{ ...INFO_LABEL_STYLE, marginBottom: 0 }}>{label}</div>
    <div style={INFO_VALUE_STYLE}>{value}</div>
  </div>
);

interface WorkflowStepItem {
  key: string;
  label: string;
}

interface OrderWorkflowRailProps {
  title: string;
  steps: WorkflowStepItem[];
  currentKey: string;
  currentLabel: string;
  cancelled?: boolean;
  cancelledText?: string;
  held?: boolean;
  heldText?: string;
}

export const OrderWorkflowRail: React.FC<OrderWorkflowRailProps> = ({
  title,
  steps,
  currentKey,
  currentLabel,
  cancelled = false,
  cancelledText,
  held = false,
  heldText,
}) => {
  const currentIndex = steps.findIndex((step) => step.key === currentKey);
  // Even rows: up to 6 steps in one row, more split into two (9 → 5 + 4), never under 150 px a tile,
  // so a label like „W przygotowaniu” is never broken inside a word.
  const perRow = steps.length <= 6 ? steps.length : Math.ceil(steps.length / 2);

  return (
    <div
      style={{
        ...SECTION_CARD_STYLE,
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        marginBottom: 16,
        background: 'var(--app-surface)',
        overflow: 'hidden',
      }}
    >
      <div style={{ padding: '14px 16px 4px' }}>
        {renderSectionTitle(title)}
      </div>
      <div
        style={{
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
          padding: '12px 16px 16px',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fill, minmax(max(150px, calc((100% - ${(perRow - 1) * 10}px) / ${perRow})), 1fr))`,
            gridAutoRows: '1fr',
            gap: 10,
            width: '100%',
          }}
        >
          {steps.map((step, index) => {
            const isCurrent = step.key === currentKey && !cancelled && !held;
            const isComplete = currentIndex >= 0 && index < currentIndex && !cancelled && !held;
            const markerBackground = isCurrent ? '#2563eb' : isComplete ? '#0f766e' : 'var(--app-border)';
            const markerColor = isCurrent || isComplete ? '#ffffff' : 'var(--app-text-muted)';

            return (
              <div
                key={step.key}
                style={{
                  minWidth: 0,
                  minHeight: 88,
                  padding: '10px 10px 9px',
                  borderRadius: 10,
                  border: isCurrent
                    ? '1px solid rgba(37, 99, 235, 0.35)'
                    : isComplete
                      ? '1px solid rgba(15, 118, 110, 0.24)'
                      : '1px solid var(--app-border)',
                  background: isCurrent
                    ? 'rgba(37, 99, 235, 0.05)'
                    : isComplete
                      ? 'rgba(15, 118, 110, 0.04)'
                      : 'var(--app-surface-hover)',
                }}
              >
                <div style={{ position: 'relative', display: 'grid', gap: 8 }}>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 28,
                      height: 28,
                      borderRadius: 999,
                      background: markerBackground,
                      color: markerColor,
                      fontSize: 13,
                      fontWeight: 600,
                      boxShadow: isCurrent ? '0 0 0 4px rgba(37, 99, 235, 0.12)' : undefined,
                    }}
                  >
                    {index + 1}
                  </span>
                  <span
                    style={{
                      display: 'block',
                      paddingRight: 8,
                      color: isCurrent ? 'var(--app-text)' : 'var(--app-text-secondary)',
                      fontSize: 13,
                      fontWeight: isCurrent ? 600 : 500,
                      lineHeight: 1.25,
                      whiteSpace: 'normal',
                    }}
                  >
                    {step.label}
                  </span>
                  {isCurrent && (
                    <span style={{ color: 'var(--app-primary)', fontSize: 12, fontWeight: 600, lineHeight: 1.2 }}>
                      {currentLabel}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {(cancelled || held || currentIndex === -1) && (
          <div
            style={{
              marginTop: 12,
              padding: '10px 12px',
              borderRadius: 8,
              background: cancelled ? '#fef2f2' : held ? '#fff7ed' : '#f8fafc',
              border: cancelled ? '1px solid #fecaca' : held ? '1px solid #fed7aa' : '1px solid #e2e8f0',
              color: cancelled ? '#b91c1c' : held ? '#c2410c' : '#475569',
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            {cancelled ? cancelledText : held ? heldText : currentLabel}
          </div>
        )}
      </div>
    </div>
  );
};
